import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
    useCrossDockOptions,
    useFacilityOptions,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { Button } from 'shadcn/components/ui/Button';
import { UnderConstruction } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

import { CrossDockPurchaseOrdersTable } from './CrossDockPurchaseOrdersTable';
import { useCrossDockPurchaseOrdersSearch } from './useCrossDockPurchaseOrdersSearch';

const CROSS_DOCK_FEES_FEATURE_FLAG = 'CROSS_DOCK_FEES';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function CrossDockPurchaseOrders() {
    const history = useHistory();
    const { data: crossDockPurchaseOrdersEnabled, isLoading: isFeatureLoading } =
        useIsFeatureOn(CROSS_DOCK_FEES_FEATURE_FLAG);

    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageIndex, setPageIndex] = useState(0);
    const offset = pageIndex * pageSize;

    const listQuery = useCrossDockPurchaseOrdersSearch(
        { limit: pageSize, offset },
        { enabled: Boolean(crossDockPurchaseOrdersEnabled) },
    );
    const { data: facilityOptions = [] } = useFacilityOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });
    const { data: crossDockOptions = [] } = useCrossDockOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });

    const facilityNameById = useMemo(
        () => new Map(facilityOptions.map((option) => [Number(option.value), option.label])),
        [facilityOptions],
    );
    const crossDockNameById = useMemo(
        () => new Map(crossDockOptions.map((option) => [Number(option.value), option.label])),
        [crossDockOptions],
    );

    const rows = listQuery.data?.rows ?? [];
    const pagination = listQuery.data?.pagination ?? {};
    const totalCount = pagination.totalCount ?? 0;
    const hasMore = Boolean(pagination.hasMore);
    const hasPrevious = pageIndex > 0;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    const handleNextPage = useCallback(() => {
        if (hasMore) {
            setPageIndex((prev) => prev + 1);
        }
    }, [hasMore]);

    const handlePreviousPage = useCallback(() => {
        setPageIndex((prev) => Math.max(0, prev - 1));
    }, []);

    const handlePageSizeChange = useCallback((newPageSize) => {
        setPageSize(newPageSize);
        setPageIndex(0);
    }, []);

    const handleCreate = useCallback(() => {
        history.push('/logistics/cross-dock-purchase-orders/create');
    }, [history]);

    if (isFeatureLoading) {
        return null;
    }

    if (!crossDockPurchaseOrdersEnabled) {
        return <UnderConstruction title="Cross Dock Purchase Orders" />;
    }

    return (
        <div className="flex min-h-screen flex-col px-4 pb-6 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-xl font-semibold">Cross Dock Purchase Orders</h1>
                <Button type="button" size="sm" onClick={handleCreate}>
                    Create Cross Dock Purchase Order
                </Button>
            </div>
            <div
                className="mt-4 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20 px-4 py-3 text-sm text-muted-foreground"
                data-testid="cross-dock-purchase-orders-filters-stub"
            />
            {listQuery.error?.message ? (
                <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                    {listQuery.error.message}
                </div>
            ) : null}
            <div className="mt-4">
                <CrossDockPurchaseOrdersTable
                    data={rows}
                    facilityNameById={facilityNameById}
                    crossDockNameById={crossDockNameById}
                    isFetching={listQuery.isFetching}
                    isLoading={listQuery.isLoading}
                    totalCount={totalCount}
                    hasNextPage={hasMore}
                    hasPrevious={hasPrevious}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    currentPage={pageIndex + 1}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
            </div>
        </div>
    );
}

export { CrossDockPurchaseOrders };
